import React from 'react';
import './Badge.css';

/**
 * Badge — unified pill replacing ad-hoc status / verification / role / turn pills.
 *
 * Props:
 *  - variant: 'status' | 'listing' | 'verification' | 'escrow' | 'role' | 'turn' | 'neutral'
 *  - value:   string — drives color + icon + default label per the maps below
 *  - children: optional override label
 *  - size:   'sm' | 'md' (default 'md')
 *  - icon:   optional ReactNode override (rendered inside an aria-hidden span)
 *  - pulse:  boolean — adds a small animated dot
 *
 * Renders nothing for unknown / null values.
 *
 * Visible label is semantic text (e.g. "ACCEPTED") so screen readers do not
 * read raw emoji. Emoji icons are wrapped in <span aria-hidden="true">.
 */

export const STATUS_BADGE_MAP = {
  pending:   { label: 'Pending',   icon: '⏳', tone: 'warning' },
  countered: { label: 'Countered', icon: '🔄', tone: 'info' },
  accepted:  { label: 'Accepted',  icon: '✅', tone: 'success' },
  rejected:  { label: 'Rejected',  icon: '❌', tone: 'error' },
  withdrawn: { label: 'Withdrawn', icon: '🚫', tone: 'neutral' },
  expired:   { label: 'Expired',   icon: '⏰', tone: 'neutral' },
  // Closed by cutthe6.com when the seller deleted the listing (legal copy §15.3).
  closed:    { label: 'Closed',    icon: '📁', tone: 'neutral' },
  // An accepted offer whose deal was recorded as ended (QA 2026-10-16).
  deal_ended: { label: 'Deal ended', icon: '📁', tone: 'neutral' },
};

export const VERIFICATION_BADGE_MAP = {
  verified:   { label: 'Verified',   icon: '✓', tone: 'success' },
  unverified: { label: 'Unverified', icon: '!', tone: 'warning' },
  skipped:    { label: 'Skipped',    icon: '–', tone: 'neutral' },
  missing:    { label: 'Missing',    icon: '⚠', tone: 'error' },
};

// Earnest-money escrow receipt on an accepted offer.
export const ESCROW_BADGE_MAP = {
  received: { label: 'Escrow receipt', icon: '✓', tone: 'success' },
  needed:   { label: 'Escrow receipt needed', icon: '!', tone: 'warning' },
};

export const ROLE_BADGE_MAP = {
  buyer:  { label: 'Buyer',  tone: 'info' },
  seller: { label: 'Seller', tone: 'warning' },
  shared: { label: 'Shared', tone: 'neutral' },
};

export const TURN_BADGE_MAP = {
  you:  { label: 'Your turn',                  tone: 'success', pulse: true },
  them: { label: 'Waiting on the other party', tone: 'neutral', pulse: false },
};

// Listing lifecycle states worth flagging on a card. Only `sold` today —
// active/pending listings read as available.
export const LISTING_BADGE_MAP = {
  sold: { label: 'Sold', icon: '🏷️', tone: 'error' },
};

const VARIANT_MAPS = {
  status:       STATUS_BADGE_MAP,
  listing:      LISTING_BADGE_MAP,
  verification: VERIFICATION_BADGE_MAP,
  escrow:       ESCROW_BADGE_MAP,
  role:         ROLE_BADGE_MAP,
  turn:         TURN_BADGE_MAP,
};

export default function Badge({
  variant = 'neutral',
  value,
  children,
  size = 'md',
  icon,
  pulse = false,
}) {
  if (value === null || value === undefined || value === 'none') return null;

  let meta = null;
  if (variant !== 'neutral') {
    const map = VARIANT_MAPS[variant];
    meta = map ? map[value] : null;
    if (!meta) return null;
  }

  const tone = meta ? meta.tone : 'neutral';
  const label = children != null ? children : (meta ? meta.label : value);
  const renderedIcon = icon !== undefined ? icon : (meta ? meta.icon : null);
  const showPulse = pulse || (meta && meta.pulse);

  const classes = [
    'ui-badge',
    `ui-badge--${variant}`,
    value ? `ui-badge--${variant}-${value}` : '',
    `ui-badge--tone-${tone}`,
    size === 'sm' ? 'ui-badge--sm' : '',
  ].filter(Boolean).join(' ');

  return (
    <span className={classes}>
      {showPulse && <span className="ui-badge__dot" aria-hidden="true" />}
      {renderedIcon && (
        <span className="ui-badge__icon" aria-hidden="true">{renderedIcon}</span>
      )}
      <span className="ui-badge__label">{label}</span>
    </span>
  );
}
